import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, FileText, Boxes, Wrench, MapPin, Users, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";

const TYPES = [
    { id: "product", label: "Product Inquiry", icon: PackageSearch, desc: "Get details, availability and pricing for specific products." },
    { id: "quote", label: "Request a Quote", icon: FileText, desc: "Share your BOQ or requirements; receive a detailed quotation." },
    { id: "amc", label: "AMC Inquiry", icon: Wrench, desc: "Comprehensive annual maintenance for your infrastructure." },
    { id: "site_survey", label: "Site Survey", icon: MapPin, desc: "Book an on-site visit and RF/cabling assessment." },
    { id: "consultation", label: "Technical Consultation", icon: Users, desc: "Talk to a senior networking engineer." },
    { id: "bulk", label: "Bulk Purchase", icon: Boxes, desc: "Special pricing for volume orders and rollouts." },
];

export default function InquiryPage() {
    const params = new URLSearchParams(useLocation().search);
    const initialType = TYPES.find((t) => t.id === params.get("type"))?.id || "quote";
    const [activeType, setActiveType] = useState(initialType);
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setActiveType(initialType);
    }, [initialType]);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const activeMeta = useMemo(() => TYPES.find((t) => t.id === activeType), [activeType]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill name, email, phone and message.");
            return;
        }
        setLoading(true);
        try {
            await submitInquiry({
                inquiry_type: activeType,
                subject: form.subject || activeMeta.label,
                ...form,
            });
            toast.success("Inquiry submitted. Our team will reach out shortly.");
            setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="inquiry-page" className="bg-white">
            <section className="relative overflow-hidden bg-navy text-white">
                <div className="absolute inset-0 opacity-[0.08]"
                     style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #1E90FF 0, transparent 40%), radial-gradient(circle at 80% 70%, #1E90FF 0, transparent 40%)" }} />
                <div className="container-lg relative py-16 lg:py-20">
                    <span className="text-xs font-bold tracking-[0.24em] uppercase text-brand">Customer Inquiry</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
                        Tell us what you need.<br />We&apos;ll get back within an hour.
                    </h1>
                </div>
            </section>

            <section className="container-lg py-10 lg:py-16">
                <div className="grid lg:grid-cols-[300px_1fr] gap-8">
                    <aside>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Inquiry type</div>
                        <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                            {TYPES.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveType(t.id)}
                                    data-testid={`inquiry-type-${t.id}`}
                                    className={`flex items-center gap-3 rounded-xl p-3.5 border text-left transition-colors ${
                                        activeType === t.id
                                            ? "border-brand bg-brand-soft/60 text-navy"
                                            : "border-slate-200 hover:border-brand/40 hover:bg-slate-50"
                                    }`}
                                >
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${activeType === t.id ? "bg-brand text-white" : "bg-brand-soft text-brand"}`}>
                                        <t.icon className="w-4 h-4" />
                                    </div>
                                    <div className="text-sm font-semibold text-navy">{t.label}</div>
                                </button>
                            ))}
                        </div>
                    </aside>

                    <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="inquiry-form">
                        <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center text-brand">
                                <activeMeta.icon className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="font-display text-xl sm:text-2xl font-bold text-navy">{activeMeta.label}</h2>
                                <p className="text-sm text-slate-500 mt-0.5">{activeMeta.desc}</p>
                            </div>
                        </div>

                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                            <Field label="Full name" required>
                                <input className={ip} value={form.name} onChange={upd("name")} data-testid="inquiry-page-input-name" />
                            </Field>
                            <Field label="Phone" required>
                                <input className={ip} value={form.phone} onChange={upd("phone")} data-testid="inquiry-page-input-phone" />
                            </Field>
                            <Field label="Email" required>
                                <input type="email" className={ip} value={form.email} onChange={upd("email")} data-testid="inquiry-page-input-email" />
                            </Field>
                            <Field label="Company">
                                <input className={ip} value={form.company} onChange={upd("company")} data-testid="inquiry-page-input-company" />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Subject">
                                    <input className={ip} value={form.subject} onChange={upd("subject")} placeholder={activeMeta.label} data-testid="inquiry-page-input-subject" />
                                </Field>
                            </div>
                            <div className="sm:col-span-2">
                                <Field label="Details" required>
                                    <textarea rows={5} className={`${ip} resize-none`} value={form.message} onChange={upd("message")} data-testid="inquiry-page-input-message" placeholder="Share your requirements, timelines, quantities, site info…" />
                                </Field>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="inquiry-page-submit"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-colors shadow-md"
                        >
                            <Send className="w-4 h-4" /> {loading ? "Sending…" : "Submit Inquiry"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
}

const ip = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-colors";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                {label} {required && <span className="text-brand">*</span>}
            </span>
            {children}
        </label>
    );
}
